import React, { useState, useEffect } from 'react';
import { X, Plus, Edit, Trash2, Save, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { apiService } from '@/services/api';
import toast from 'react-hot-toast';

interface Category {
  id: string;
  name: string;
  description?: string;
  parentId?: string;
  isActive: boolean;
  parent?: Category;
  children?: Category[];
  createdAt: string;
  updatedAt: string;
  _count?: {
    items: number;
    children: number;
  };
}

interface CategoryStat {
  categoryId: string;
  categoryName: string;
  itemCount: number;
}

interface CategoryManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCategoryChange?: () => void;
}

const CategoryManagementModal: React.FC<CategoryManagementModalProps> = ({
  isOpen,
  onClose,
  onCategoryChange
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryStats, setCategoryStats] = useState<CategoryStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', parentId: '' });

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      fetchCategoryStats();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const response = await apiService.get<Category[]>('/categories');
      console.log('Loaded categories:', response); // Debug-Log
      setCategories(response || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Fehler beim Laden der Kategorien');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryStats = async () => {
    try {
      const response = await apiService.get<CategoryStat[]>('/categories/stats');
      setCategoryStats(response || []);
    } catch (error) {
      console.error('Error fetching category stats:', error);
    }
  };

  const handleCreateCategory = async () => {
    if (!formData.name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }

    try {
      await apiService.post('/categories', formData);
      toast.success('Kategorie erfolgreich erstellt');
      fetchCategories();
      fetchCategoryStats();
      setIsCreating(false);
      setFormData({ name: '', description: '', parentId: '' });
      onCategoryChange?.();
    } catch (error: any) {
      console.error('Error creating category:', error);
      const message = error?.response?.data?.error || error.message || 'Fehler beim Erstellen der Kategorie';
      toast.error(message);
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory || !formData.name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }

    try {
      await apiService.put(`/categories/${editingCategory.id}`, formData);
      toast.success('Kategorie erfolgreich aktualisiert');
      fetchCategories();
      fetchCategoryStats();
      setEditingCategory(null);
      setFormData({ name: '', description: '', parentId: '' });
      onCategoryChange?.();
    } catch (error: any) {
      console.error('Error updating category:', error);
      const message = error?.response?.data?.error || error.message || 'Fehler beim Aktualisieren der Kategorie';
      toast.error(message);
    }
  };

  const handleDeleteCategory = async (category: Category) => {
    const itemCount = categoryStats.find(stat => stat.categoryId === category.id)?.itemCount || 0;
    
    if (itemCount > 0) {
      toast.error(`Die Kategorie kann nicht gelöscht werden, da sie von ${itemCount} Item(s) verwendet wird`);
      return;
    }

    if (!confirm(`Möchten Sie die Kategorie "${category.name}" wirklich löschen?`)) {
      return;
    }

    try {
      await apiService.delete(`/categories/${category.id}`);
      toast.success('Kategorie erfolgreich gelöscht');
      fetchCategories();
      fetchCategoryStats();
      onCategoryChange?.();
    } catch (error: any) {
      console.error('Error deleting category:', error);
      const message = error?.response?.data?.error || error.message || 'Fehler beim Löschen der Kategorie';
      toast.error(message);
    }
  };

  const handleStartEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({ 
      name: category.name, 
      description: category.description || '', 
      parentId: category.parentId || '' 
    });
    setIsCreating(false);
  };

  const handleStartCreate = (parentId?: string) => {
    setIsCreating(true);
    setEditingCategory(null);
    setFormData({ name: '', description: '', parentId: parentId || '' });
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
    setIsCreating(false);
    setFormData({ name: '', description: '', parentId: '' });
  };

  const getItemCount = (categoryId: string): number => {
    return categoryStats.find(stat => stat.categoryId === categoryId)?.itemCount || 0;
  };

  // Erstelle flache Liste aller Kategorien für Parent-Auswahl
  const flattenCategories = (cats: Category[]): Category[] => {
    const result: Category[] = [];
    const addCategory = (cat: Category, level: number = 0) => {
      const displayName = '  '.repeat(level) + cat.name;
      result.push({ ...cat, name: displayName });
      if (cat.children && cat.children.length > 0) {
        cat.children.forEach(child => addCategory(child, level + 1));
      }
    };
    cats.forEach(cat => addCategory(cat));
    console.log('Flattened categories:', result); // Debug-Log
    return result;
  };

  const flatCategories = flattenCategories(categories);

  // Rendere Kategorie mit Hierarchie
  const renderCategory = (category: Category, level: number = 0) => {
    const itemCount = category._count?.items || getItemCount(category.id);
    const childrenCount = category._count?.children || (category.children?.length || 0);
    
    console.log(`Rendering category: ${category.name} at level ${level}, children: ${childrenCount}`); // Debug-Log

    return (
      <div key={category.id}>
        <Card className="p-4" style={{ marginLeft: `${level * 20}px` }}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h4 className="font-medium">
                  {level > 0 && <span className="text-muted-foreground mr-2">{'  '.repeat(level)}└─</span>}
                  {category.name}
                </h4>
                <div className="flex gap-2">
                  <Badge variant="secondary">
                    {itemCount} Items
                  </Badge>
                  {childrenCount > 0 && (
                    <Badge variant="outline">
                      {childrenCount} Unterkategorien
                    </Badge>
                  )}
                  <Badge variant="outline" className="text-xs">
                    Level {level + 1}
                  </Badge>
                  {!category.isActive && (
                    <Badge variant="destructive">Inaktiv</Badge>
                  )}
                </div>
              </div>
              {category.description && (
                <p className="text-sm text-muted-foreground mt-1">
                  {category.description}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleStartCreate(category.id)}
                disabled={isCreating || editingCategory !== null}
                title="Unterkategorie hinzufügen"
              >
                <Plus className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleStartEdit(category)}
                disabled={isCreating || editingCategory !== null}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeleteCategory(category)}
                disabled={
                  itemCount > 0 || 
                  childrenCount > 0 || 
                  isCreating || 
                  editingCategory !== null
                }
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Rendere Unterkategorien rekursiv */}
        {category.children && category.children.length > 0 && (
          <div className="ml-4">
            {category.children.map(child => 
              renderCategory(child, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">Kategorienverwaltung</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Header with Add Button */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg font-medium">Kategorien ({categories.length})</h3>
              <p className="text-sm text-muted-foreground">
                Verwalten Sie Ihre Produktkategorien
              </p>
            </div>
            <Button onClick={() => handleStartCreate()} disabled={isCreating || editingCategory !== null}>
              <Plus className="h-4 w-4 mr-2" />
              Neue Kategorie
            </Button>
          </div>

          {/* Create/Edit Form */}
          {(isCreating || editingCategory) && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-base">
                  {isCreating ? 'Neue Kategorie erstellen' : `Kategorie "${editingCategory?.name}" bearbeiten`}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Name *
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Kategoriename eingeben"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Beschreibung
                    </label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Optionale Beschreibung eingeben"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Übergeordnete Kategorie
                    </label>
                    <select
                      value={formData.parentId}
                      onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
                      className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
                      disabled={editingCategory?.id ? 
                        flatCategories.some(cat => cat.id === editingCategory.id && cat.children?.length) : 
                        false
                      }
                    >
                      <option value="">Keine (Hauptkategorie)</option>
                      {flatCategories
                        .filter(cat => editingCategory ? cat.id !== editingCategory.id : true)
                        .filter(cat => !editingCategory || !isDescendantOf(cat.id, editingCategory.id))
                        .map(cat => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))
                      }
                    </select>
                    {editingCategory?.children?.length && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Diese Kategorie hat Unterkategorien und kann daher nicht verschoben werden.
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button 
                      onClick={isCreating ? handleCreateCategory : handleUpdateCategory}
                      disabled={!formData.name.trim()}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {isCreating ? 'Erstellen' : 'Speichern'}
                    </Button>
                    <Button variant="outline" onClick={handleCancelEdit}>
                      <XCircle className="h-4 w-4 mr-2" />
                      Abbrechen
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Categories List */}
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-3">
              {categories.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <p>Keine Kategorien gefunden</p>
                  <p className="text-sm">Erstellen Sie eine neue Kategorie, um zu beginnen</p>
                </div>
              ) : (
                // Rendere nur Hauptkategorien, Unterkategorien werden rekursiv gerendert
                categories
                  .filter(cat => !cat.parentId)
                  .map(category => renderCategory(category))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Hilfsfunktion um zu prüfen ob eine Kategorie Nachfahre einer anderen ist
  function isDescendantOf(categoryId: string, potentialAncestorId: string): boolean {
    const category = categories.find(c => c.id === categoryId);
    if (!category || !category.parentId) return false;
    if (category.parentId === potentialAncestorId) return true;
    return isDescendantOf(category.parentId, potentialAncestorId);
  }
};

export default CategoryManagementModal;
